import { HStack, Icon, VStack, Text, Box } from "@chakra-ui/react";
import { capitalize, toLower } from "lodash";
import { IconType } from "react-icons";
import { FaClock, FaUserClock } from "react-icons/fa";
import {
  CAMPAIGN_QUALIFICATION_RULES,
  QUALIFICATION_RULES_OPTIONS,
  RECURRING_FREQUENCIES,
} from "../../../../../../common/constants/campaign";
import { addSuffixForPlural } from "../../../../../../common/helper/commonHelper";
import {
  CampaignRuleResp,
  CampaignSchedule,
  RUN_TYPE,
} from "../../../../../../common/types/campaign";
import { formatDateString } from "../../../../../../components/DateTimeRangeFilter";

function formatTime(time: string) {
  const [hourString, minute] = time.split(":");
  const hour = +hourString % 24;
  return (hour % 12 || 12) + ":" + minute + (hour < 12 ? " AM" : " PM");
}

const englishOrdinalRules = new Intl.PluralRules("en", { type: "ordinal" });

const suffixes = {
  zero: "",
  one: "st",
  two: "nd",
  few: "rd",
  many: "th",
  other: "th",
};

function suffixOrdinal(number: number) {
  const category = englishOrdinalRules.select(number);
  const suffix = suffixes[category];
  return number + suffix;
}

const buildFrequencyRange = (data: CampaignSchedule) => {
  let text = "Run ";

  if (data.run_type === RUN_TYPE.ONCE) {
    text += "once";
  } else if (data.run_type === RUN_TYPE.AT_ONCE) {
    text += "now";
  } else if (data.run_type === RUN_TYPE.RECURRING) {
    switch (data.run_config.frequency) {
      case RECURRING_FREQUENCIES.DAY:
        text += `every ${
          data.run_config.freq_options.unit ?? ""
        } ${addSuffixForPlural(
          data.run_config.frequency,
          data.run_config.freq_options.unit
        )}`;
        break;
      case RECURRING_FREQUENCIES.WEEK:
        text += `every week`;
        if (data.run_config.freq_options.day) {
          text += ` on ${capitalize(data.run_config.freq_options.day)}`;
        }
        break;
      case RECURRING_FREQUENCIES.MONTH:
        if (data.run_config.freq_options.date_string === "custom") {
          text += `every ${suffixOrdinal(
            Number(data.run_config.freq_options.day)
          )} day of the month`;
        } else {
          text += `every ${
            data.run_config.freq_options.date_string?.split("_")[0]
          } day of the month`;
        }
        break;
      case RECURRING_FREQUENCIES.YEAR:
        if (data.run_config.freq_options.date) {
          text += ` on ${formatDateString(data.run_config.freq_options.date)}`;
        }
        break;
    }
  } else if (data.run_type === RUN_TYPE.PERIOD) {
    if (data.run_config.frequency) {
      text += `every ${data.run_config.unit ?? ""} ${
        data.run_config.frequency
      }`;
    }
  }

  if (data.run_type !== RUN_TYPE.PERIOD && data.run_config.run_time) {
    text += ` at ${formatTime(data.run_config.run_time)}`;
  }

  if (text === "Run ") {
    text = "Not scheduled";
  }

  return text.trim();
};

const buildTimeRange = (data: CampaignSchedule) => {
  let text = "";
  if (data.run_type !== RUN_TYPE.ONCE && data.run_type !== RUN_TYPE.AT_ONCE) {
    if (data.run_config.start_date) {
      text += `From ${formatDateString(data.run_config.start_date + "T00:00")}`;
    }

    if (data.run_config.end_date) {
      if (data.run_config.start_date) {
        text += ` to ${formatDateString(data.run_config.end_date + "T00:00")}`;
      } else {
        text = `Till ${formatDateString(data.run_config.end_date + "T00:00")}`;
      }
    }
  }
  return text;
};

function buildQualifyRulesText(rule: CampaignRuleResp) {
  let text = "";

  if (
    rule.rule === CAMPAIGN_QUALIFICATION_RULES.EVERY_TIME ||
    rule.rule === CAMPAIGN_QUALIFICATION_RULES.ONCE
  ) {
    text += `Qualify contacts ${toLower(
      QUALIFICATION_RULES_OPTIONS[rule.rule]
    )}`;
  } else if (rule.rule === CAMPAIGN_QUALIFICATION_RULES.ONCE_EVERY) {
    if (rule.recurring_period && rule.recurring_period_unit)
      text += `Qualify contacts ${toLower(
        QUALIFICATION_RULES_OPTIONS[rule.rule]
      )} ${rule.recurring_period} ${addSuffixForPlural(
        rule.recurring_period_unit?.slice(0, -1),
        rule.recurring_period
      )}`;
  }

  return text;
}

function IconWithTextAndDesc({
  icon,
  label,
  description,
}: {
  icon: IconType;
  label: string;
  description?: string;
}) {
  return (
    <HStack w="100%">
      <VStack alignItems="flex-start" spacing="0">
        <HStack>
          <Icon color="blue.700" as={icon} />
          <Text color="gray.800" fontSize="sm">
            {label}
          </Text>
        </HStack>
        <Box>
          <Text
            ml="25px"
            fontWeight="bold"
            fontSize="xs"
            color="oneOffs.subtitle"
          >
            {description}
          </Text>
        </Box>
      </VStack>
    </HStack>
  );
}

export default function CampaignScheduleReadonly({
  data,
  rule,
}: {
  data: CampaignSchedule;
  rule: CampaignRuleResp;
}) {
  return (
    <VStack alignItems="flex-start" w="100%">
      <IconWithTextAndDesc
        label={buildFrequencyRange(data)}
        description={buildTimeRange(data)}
        icon={FaClock}
      />
      <IconWithTextAndDesc
        label={buildQualifyRulesText(rule)}
        description=""
        icon={FaUserClock}
      />
    </VStack>
  );
}
